import React from "react";
import { Checkbox, TableContainer, TableHead, TableRow, TableBody, Table, Paper } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { SmallCell, LargeCell } from "../style/tablecell";
import { TypographyNomalBold } from '../style/typography';
import { BaseFormControlLabel } from '../style/formControl';
import { validationSchemaCheckSumary } from '../func/validation_check';
import { CustomTextFieldElementCheckSummary } from '../components/CustomTextFieldElement';

// 表の行データ定義
const getTableRows = (type) => [
  // ダム本体 - 堤体 - 天端設備
  {
    id: "body_top_crack",
    cells: [
      { content: "ダム本体", rowSpan: 5 },
      { content: "堤体", rowSpan: 4 },
      { content: "天端設備", rowSpan: 2 },
      { content: "クラック" }
    ],
    checkboxKey: "is_body_top_crack",
    noteKey: "is_body_top_crack_note"
  },
  {
    id: "body_top_facility",
    cells: [
      { content: "付属設備" }
    ],
    checkboxKey: "is_body_top_facility",
    noteKey: "is_body_top_facility_note"
  },
  // ダム本体 - 堤体 - 上下流面
  {
    id: "body_surface_crack",
    cells: [
      { content: "上下流面", rowSpan: 2 },
      { content: "クラック" }
    ],
    checkboxKey: "is_body_surface_crack",
    noteKey: "is_body_surface_crack_note"
  },
  {
    id: "body_surface_leak",
    cells: [
      { content: "漏水" }
    ],
    checkboxKey: "is_body_surface_leak",
    noteKey: "is_body_surface_leak_note"
  },
  // ダム本体 - その他
  {
    id: "dam_body_other",
    cells: [
      { content: "その他" },
      { content: "" },
      { content: "" }
    ],
    checkboxKey: "dam_body_other",
    noteKey: "dam_body_other_note"
  },
  // 貯水池及び取付部周辺
  {
    id: "reservoir_slope_landslide",
    cells: [
      { content: "貯水池及び取付部周辺", rowSpan: 2 },
      { content: "貯水池" },
      { content: "貯水池法面" },
      { content: "地すべり" }
    ],
    checkboxKey: "is_reservoir_slope_landslide",
    noteKey: "is_reservoir_slope_landslide_note"
  },
  {
    id: "reservoir_other",
    cells: [
      { content: "その他" },
      { content: "" },
      { content: "" }
    ],
    checkboxKey: "reservoir_other",
    noteKey: "reservoir_other_note"
  },
  // 放流設備 - ゲート - ゲート周辺
  {
    id: "gate_crack",
    cells: [
      { content: "放流設備", rowSpan: 4 },
      { content: "ゲート", rowSpan: 3 },
      { content: "ゲート周辺", rowSpan: 2 },
      { content: "クラック" }
    ],
    checkboxKey: "is_gate_crack",
    noteKey: "is_gate_crack_note"
  },
  {
    id: "gate_leak",
    cells: [
      { content: "漏水" }
    ],
    checkboxKey: "is_gate_leak",
    noteKey: "is_gate_leak_note"
  },
  // 放流設備 - ゲート - 機器
  {
    id: "gate_equipment_anomaly",
    cells: [
      { content: "機器" },
      { content: "異常" }
    ],
    checkboxKey: "is_gate_equipment_anomaly",
    noteKey: "is_gate_equipment_anomaly_note"
  },
  // 放流設備 - その他
  {
    id: "gate_other",
    cells: [
      { content: "その他" },
      { content: "" },
      { content: "" }
    ],
    checkboxKey: "gate_other",
    noteKey: "gate_other_note"
  },
  // 通信警報その他
  {
    id: "communication_equipment",
    cells: [
      { content: "通信警報その他", rowSpan: 5 },
      { content: "通信設備" },
      { content: "" },
      { content: "設備点検" }
    ],
    checkboxKey: "is_communication_equipment_anomaly",
    noteKey: "is_communication_equipment_anomaly_note"
  },
  {
    id: "observation_equipment",
    cells: [
      { content: "観測設備" },
      { content: "" },
      { content: "設備点検" }
    ],
    checkboxKey: "is_observation_equipment_anomaly",
    noteKey: "is_observation_equipment_anomaly_note"
  },
  {
    id: "alarm_equipment",
    cells: [
      { content: "警報設備" },
      { content: "" },
      { content: "設備点検" }
    ],
    checkboxKey: "is_alarm_equipment_anomaly",
    noteKey: "is_alarm_equipment_anomaly_note"
  },
  {
    id: "backup_power",
    cells: [
      { content: "予備電源" },
      { content: "" },
      { content: "設備点検" }
    ],
    checkboxKey: "is_backup_power_anomaly",
    noteKey: "is_backup_power_anomaly_note"
  },
  {
    id: "other_equipment",
    cells: [
      { content: "その他" },
      { content: "" },
      { content: "設備点検" }
    ],
    checkboxKey: "is_other_equipment_anomaly",
    noteKey: "is_other_equipment_anomaly_note"
  },
  {
    id: "inspection_detail",
    cells: [
      { content: "詳細な外観点検結果" },
      { content: "" },
      { content: "" },
      { content: "" }
    ],
    checkboxKey: "inspection_detail_result",
    noteKey: "inspection_detail_result_note"
  },
];

const DamInspectionResult = ({ checkSummary, setCheckSummary, isReadOnly, type }) => {
  const { control, handleSubmit, getValues, setValue, formState: { errors }, trigger } = useForm({
    defaultValues: { checkSummarys: checkSummary },
    resolver: yupResolver(validationSchemaCheckSumary),
    mode: "onBlur"
  });

  // チェック状態を常に逆にする
  const handleChange = (key, type, relatedInputKey) => (event) => {
    if (type === "checkbox") {
      // チェックボックスの場合は反転させて設定
      const newValue = !event.target.checked;
      setCheckSummary((prevState) => ({
        ...prevState,
        [key]: newValue,
        [relatedInputKey]: "",
      }));

      setValue(`checkSummarys.${key}`, newValue);
      setValue(`checkSummarys.${relatedInputKey}`, "");
      trigger(`checkSummarys.${relatedInputKey}`);
    } else if (type === "input") {
      // 入力フィールドの場合は値をそのまま設定
      setCheckSummary((prevState) => ({
        ...prevState,
        [key]: event.target.value,
      }));
    }
  };

  const tableRows = getTableRows(type);

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <SmallCell><TypographyNomalBold>項目</TypographyNomalBold></SmallCell>
            <SmallCell><TypographyNomalBold>位置</TypographyNomalBold></SmallCell>
            <SmallCell><TypographyNomalBold>点検設備</TypographyNomalBold></SmallCell>
            <SmallCell><TypographyNomalBold>点検項目</TypographyNomalBold></SmallCell>
            <SmallCell><TypographyNomalBold>点検結果</TypographyNomalBold></SmallCell>
            <LargeCell><TypographyNomalBold>状況（目視等）</TypographyNomalBold></LargeCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map((row) => (
            <TableRow key={row.id}>
              {/* 最初の4列（項目、位置、点検設備、点検項目）を描画 */}
              {row.cells.map((cell, index) => (
                <SmallCell key={index} {...(cell.rowSpan && { rowSpan: cell.rowSpan })}>
                  {cell.content}
                </SmallCell>
              ))}
              
              {/* 点検結果列（チェックボックス） */}
              <SmallCell>
                {isReadOnly ? (
                  !checkSummary?.[row.checkboxKey] ? "外観 異常なし" : "-"
                ) : (
                  <BaseFormControlLabel
                    control={
                      <Checkbox 
                        checked={!checkSummary[row.checkboxKey]} 
                        onChange={handleChange(row.checkboxKey, "checkbox", row.noteKey)} 
                      />
                    }
                    label="外観 異常なし"
                  />
                )}
              </SmallCell>
              
              {/* 状況（目視等）列（テキストフィールド） */}
              <LargeCell>
                {isReadOnly ? (
                  checkSummary?.[row.noteKey] || ""
                ) : (
                  <CustomTextFieldElementCheckSummary
                    name={row.noteKey}
                    control={control}
                    disabled={!checkSummary[row.checkboxKey]}
                    onChange={(e) => handleChange(row.noteKey, "input")(e)}
                    errors={errors}
                  />
                )}
              </LargeCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DamInspectionResult;